import * as RadixDialog from '@radix-ui/react-dialog';
import { BookOpen, X } from 'lucide-react';
import { useEffect, useRef, type MouseEvent } from 'react';
import { cx } from '../cx.js';

export interface SezioneManuale {
  id: string;
  titolo: string;
  area: string;
  /** HTML già generato dal Markdown del manuale (contenuto nostro, non dell'utente). */
  html: string;
}

export interface TestiManualeDialog {
  titolo: string;
  chiudi: string;
  indice: string;
  scorciatoia: string;
}

export const TESTI_MANUALE_DIALOG: TestiManualeDialog = {
  titolo: 'Manuale',
  chiudi: 'Chiudi il manuale',
  indice: 'Sezioni del manuale',
  scorciatoia: 'Scorciatoia: premi ? da qualsiasi pagina per aprire il manuale.',
};

export interface ManualeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sezioni: SezioneManuale[];
  sezioneId: string | null;
  onSezioneChange: (id: string) => void;
  testi?: Partial<TestiManualeDialog>;
}

/**
 * Il manuale in una finestra modale: indice delle sezioni a sinistra (sotto, come menu, su telefono)
 * e contenuto che scorre a destra. Esc e il clic fuori chiudono; i link interni cambiano sezione.
 */
export function ManualeDialog({ open, onOpenChange, sezioni, sezioneId, onSezioneChange, testi: testiProp }: ManualeDialogProps) {
  const t = { ...TESTI_MANUALE_DIALOG, ...testiProp };
  const sezione = sezioni.find((s) => s.id === sezioneId) ?? sezioni[0] ?? null;
  const contenutoRef = useRef<HTMLDivElement>(null);

  // Cambio di sezione: il contenuto riparte dall'alto.
  useEffect(() => {
    if (contenutoRef.current) contenutoRef.current.scrollTop = 0; // scrollTo non esiste in jsdom
  }, [sezione?.id, open]);

  function clickNelContenuto(e: MouseEvent<HTMLDivElement>) {
    const a = (e.target as HTMLElement).closest('a');
    if (!a) return;
    const href = a.getAttribute('href') ?? '';
    if (href.startsWith('#')) {
      const id = href.slice(1);
      if (sezioni.some((s) => s.id === id)) {
        e.preventDefault();
        onSezioneChange(id);
      }
    } else if (/^https?:/.test(href)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <RadixDialog.Content
          aria-describedby={undefined}
          data-manuale
          className={cx(
            'fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2*var(--space-6))] max-w-[1100px] h-[90dvh]',
            'bg-surface rounded-lg shadow-overlay flex flex-col focus:outline-none overflow-hidden',
          )}
        >
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <BookOpen aria-hidden size={20} strokeWidth={1.75} className="text-accent shrink-0" />
              <RadixDialog.Title className="t-title-3 truncate">
                <span className="text-ink-muted">{t.titolo} · </span>
                {sezione?.titolo ?? ''}
              </RadixDialog.Title>
            </div>
            <RadixDialog.Close
              aria-label={t.chiudi}
              className="h-11 w-11 -mr-2 shrink-0 inline-flex items-center justify-center rounded-md text-ink-muted hover:text-ink hover:bg-surface-sunken"
            >
              <X aria-hidden size={20} strokeWidth={1.75} />
            </RadixDialog.Close>
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)]">
            <nav aria-label={t.indice} className="border-b md:border-b-0 md:border-r border-border bg-surface-sunken shrink-0 md:overflow-y-auto">
              <label className="md:hidden block p-3">
                <span className="sr-only">{t.indice}</span>
                <select
                  value={sezione?.id ?? ''}
                  onChange={(e) => onSezioneChange(e.target.value)}
                  className="w-full h-11 rounded-md border border-border-strong bg-surface px-3 t-body text-ink"
                >
                  {sezioni.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.titolo}
                    </option>
                  ))}
                </select>
              </label>
              <ul className="hidden md:flex flex-col gap-1 p-3">
                {sezioni.map((s) => {
                  const attiva = s.id === sezione?.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => onSezioneChange(s.id)}
                        aria-current={attiva ? 'page' : undefined}
                        className={cx(
                          'w-full text-left px-3 py-2 min-h-[44px] rounded-md t-body-small',
                          attiva ? 'bg-surface text-ink font-semibold border-l-2 border-accent' : 'text-ink-muted hover:text-ink hover:bg-surface',
                        )}
                      >
                        {s.titolo}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div
              ref={contenutoRef}
              tabIndex={0}
              onClick={clickNelContenuto}
              className="overflow-y-auto overscroll-contain px-6 py-6 md:px-10 focus-visible:outline-focus"
              data-manuale-contenuto
            >
              {/* HTML generato a build time dal Markdown del manuale: contenuto nostro, non dell'utente */}
              {sezione && <article className="manuale-contenuto" dangerouslySetInnerHTML={{ __html: sezione.html }} />}
              <p className="t-caption text-ink-muted mt-10 pt-4 border-t border-border">{t.scorciatoia}</p>
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
